import { t as __exportAll } from "./chunk-CfYAbeIz.mjs";
import * as R from "ramda";
import { assoc, assocPath, clone, concat, curry, difference, equals, filter, find, findIndex, head, insertAll, invertObj, join, keys, last, map, mapObjIndexed, mergeRight, omit, path, pathEq, pick, pluck, prepend, propEq, reduce, reject, replace, tail, values, without } from "ramda";
import { createValidator } from "@wayfinder/json-schemas/ajv-extensions";
import * as Vms from "@wayfinder/json-schemas/vms";
import piexif from "piexifjs";
import moment from "moment";
//#region src/data-model/category.ts
var category_exports = /* @__PURE__ */ __exportAll({
	asDropdownOptions: () => asDropdownOptions,
	createBadgeDropdown: () => createBadgeDropdown,
	createV4BadgeDropdown: () => createV4BadgeDropdown,
	fromCategoryList: () => fromCategoryList,
	fromTheme: () => fromTheme,
	fromV4CategoryToIcon: () => fromV4CategoryToIcon,
	fromV4IconToValue: () => fromV4IconToValue,
	fromV5CategoryToIcon: () => fromV5CategoryToIcon,
	getCategoryFromTheme: () => getCategoryFromTheme,
	getIconFromV4Value: () => getIconFromV4Value,
	getName: () => getName,
	getUrlFromCategory: () => getUrlFromCategory,
	getV4Names: () => getV4Names,
	toCategory: () => toCategory
});
const asDropdownOption = ({ canonicalName, label }) => ({
	label,
	value: canonicalName
});
const asDropdownOptions = (categories) => map(asDropdownOption, categories);
const getName = (category) => category.canonicalName;
const getV4Names = (category) => category?.v4CanonicalNames;
const getUrlFromCategory = (category) => category?.badge;
const createBadgeDropdown = (category, venueCategory, venueId, accountId) => {
	return {
		badge: `https://content.locuslabs.com/${venueCategory}/badge/${venueId}/${accountId}/${category.canonicalName}.svg`,
		label: category.label,
		value: category.canonicalName
	};
};
const v4Dropdown = (canonicalName) => {
	const label = canonicalName.replace("v4", "");
	return {
		badge: `https://content.locuslabs.com/vms-ui/category-icons/${canonicalName}.svg`,
		label,
		value: canonicalName
	};
};
const createV4BadgeDropdown = (v4CanonicalNames) => map(v4Dropdown, v4CanonicalNames);
const fromCategoryList = (canonicalName, categories) => {
	return find((c) => c.canonicalName === canonicalName, categories) || null;
};
const fromV4IconToValue = (icon) => invertObj(V4_BADGE_MAP)[icon];
const fromV4CategoryToIcon = (v4Category, updates) => {
	if (!v4Category) return updates;
	const { v4CanonicalNames } = v4Category;
	if (!v4CanonicalNames) return updates;
	if (v4CanonicalNames.length === 1) {
		const first = v4CanonicalNames[0];
		const icon = first !== void 0 ? V4_BADGE_MAP[first] : void 0;
		if (icon !== void 0) updates = assoc("icon", icon, updates);
	}
	return updates;
};
const fromV5CategoryToIcon = (updates) => assoc("icon", updates.category !== void 0 ? V5_BADGE_MAP[updates.category] : void 0, updates);
const getIconFromV4Value = (v4Value) => V4_BADGE_MAP[v4Value];
const toCategory = (venueId) => (categoryData, canonicalName) => {
	const topLevelCategory = canonicalName.includes(".") ? canonicalName.split(".")[0] ?? canonicalName : canonicalName;
	const safeData = categoryData || {};
	return {
		canonicalName,
		hasBadge: safeData.hasBadge || false,
		internalOnly: safeData.internalOnly || false,
		label: canonicalName,
		v4CanonicalNames: V4[topLevelCategory] ?? void 0,
		venueId
	};
};
const getCategoryFromTheme = (venueId, canonicalName, theme) => {
	const category = theme ? theme[canonicalName] : null;
	return category ? toCategory(venueId)(category, canonicalName) : {};
};
const fromTheme = (venueId, theme) => {
	return values(mapObjIndexed(toCategory(venueId), theme));
};
const V4 = {
	art: ["v4services"],
	baggage: ["v4bagclaim", "v4baggage-wrap"],
	bus: ["v4bus"],
	door: ["v4exit"],
	"check-in": ["v4ticketing"],
	eat: ["v4eat", "v4vending"],
	elevator: ["v4elevator"],
	escalator: ["v4escalator"],
	gate: ["v4gate"],
	parking: ["v4parking"],
	power: ["v4charging"],
	ramp: ["v4ramp"],
	relax: [
		"v4prayerroom",
		"v4relax",
		"v4tv"
	],
	restroom: [
		"v4restroom",
		"v4restroommen",
		"v4restroomwomen"
	],
	rideshare: ["v4car", "v4uber"],
	services: [
		"v4aed",
		"v4airportinfo",
		"v4atm",
		"v4bagcart",
		"v4casino",
		"v4currencyexch-dollar",
		"v4currencyexch-euro",
		"v4mailbox",
		"v4medical",
		"v4movierental",
		"v4newspaper",
		"v4nursing",
		"v4petrelief",
		"v4police",
		"v4services",
		"v4shoeshine",
		"v4smoking",
		"v4valet",
		"v4wheelchair"
	],
	shop: ["v4shop"],
	stairs: ["v4stairs"],
	terminal: ["v4gate"],
	train: ["v4train"],
	transport: [
		"v4bus",
		"v4car",
		"v4exit",
		"v4ramp",
		"v4security",
		"v4train",
		"v4walkway"
	],
	water: ["v4drinking-fountain"]
};
const V4_BADGE_MAP = {
	v4aed: "querywin-badge-aed@2x.png",
	v4airportinfo: "querywin-icon-airportinfo@2x.png",
	v4atm: "querywin-icon-atm@2x.png",
	v4bagcart: "querywin-icon-bagcart@2x.png",
	v4bagclaim: "querywin-icon-bagclaim@2x.png",
	"v4baggage-wrap": "querywin-badge-baggage-wrap@2x.png",
	v4bus: "querywin-icon-bus@2x.png",
	v4car: "querywin-icon-car@2x.png",
	v4casino: "querywin-icon-casino@2x.png",
	v4charging: "querywin-icon-charging@2x.png",
	"v4currencyexch-dollar": "querywin-icon-currencyexch-dollar@2x.png",
	"v4currencyexch-euro": "querywin-icon-currencyexch-euro@2x.png",
	"v4drinking-fountain": "querywin-badge-drinking-fountain@2x.png",
	v4eat: "querywin-icon-eat@2x.png",
	v4elevator: "querywin-icon-elevator@2x.png",
	v4escalator: "querywin-icon-escalator@2x.png",
	v4exit: "querywin-icon-exit@2x.png",
	v4gate: "querywin-icon-gate@2x.png",
	v4mailbox: "querywin-icon-mailbox@2x.png",
	v4medical: "querywin-icon-medical@2x.png",
	v4movierental: "querywin-icon-movierental@2x.png",
	v4newspaper: "querywin-badge-newspaper@2x.png",
	v4nursing: "querywin-badge-nursing@2x.png",
	v4parking: "querywin-icon-parking@2x.png",
	v4petrelief: "querywin-badge-petrelief@2x.png",
	v4police: "querywin-icon-police@2x.png",
	v4prayerroom: "querywin-icon-prayerroom@2x.png",
	v4ramp: "querywin-icon-ramp@2x.png",
	v4relax: "querywin-icon-relax@2x.png",
	v4restroom: "querywin-icon-restroom@2x.png",
	v4restroommen: "querywin-icon-restroommen@2x.png",
	v4restroomwomen: "querywin-icon-restroomwomen@2x.png",
	v4security: "querywin-icon-security@2x.png",
	v4services: "querywin-icon-services@2x.png",
	v4shoeshine: "querywin-icon-shoeshine@2x.png",
	v4shop: "querywin-icon-shop@2x.png",
	v4smoking: "querywin-icon-smoking@2x.png",
	v4stairs: "querywin-icon-stairs@2x.png",
	v4ticketing: "querywin-icon-ticketing@2x.png",
	v4train: "querywin-icon-train@2x.png",
	v4tv: "querywin-badge-tv@2x.png",
	v4uber: "querywin-badge-uber@2x.png",
	v4valet: "querywin-icon-valet@2x.png",
	v4vending: "querywin-icon-vending@2x.png",
	v4walkway: "querywin-icon-walkway@2x.png",
	v4wheelchair: "querywin-icon-wheelchair@2x.png"
};
const V5_BADGE_MAP = {
	art: "querywin-icon-services@2x.png",
	baggage: "querywin-icon-bagclaim@2x.png",
	"baggage.cart": "querywin-icon-bagcart@2x.png",
	"baggage.claim": "querywin-icon-bagclaim@2x.png",
	"baggage.drop": "querywin-icon-bagclaim@2x.png",
	"baggage.office": "querywin-icon-bagclaim@2x.png",
	"baggage.oversize": "querywin-icon-bagclaim@2x.png",
	"baggage.wrap": "querywin-icon-bagclaim@2x.png",
	checkin: "querywin-icon-ticketing@2x.png",
	"checkin.curbside": "querywin-icon-ticketing@2x.png",
	eat: "querywin-icon-eat@2x.png",
	"eat.bar": "querywin-icon-eat@2x.png",
	"eat.coffee": "querywin-icon-eat@2x.png",
	"eat.vending": "querywin-icon-eat@2x.png",
	gate: "querywin-icon-gate@2x.png",
	"gate.arrival": "querywin-icon-gate@2x.png",
	"gate.departures": "querywin-icon-gate@2x.png",
	parking: "querywin-icon-parking@2x.png",
	"parking.accessible": "querywin-icon-parking@2x.png",
	"parking.ev": "querywin-icon-parking@2x.png",
	portal: "querywin-icon-elevator@2x.png",
	"portal.bus": "querywin-icon-bus@2x.png",
	"portal.train": "querywin-icon-train@2x.png",
	"portal.elevator": "querywin-icon-elevator@2x.png",
	"portal.escalator": "querywin-icon-escalator@2x.png",
	"portal.movingwalkway": "querywin-icon-walkway@2x.png",
	"portal.ramp": "querywin-icon-walkway@2x.png",
	"portal.stairs": "querywin-icon-stairs@2x.png",
	relax: "querywin-icon-relax@2x.png",
	"relax.chair": "querywin-icon-relax@2x.png",
	"relax.chairdetail": "querywin-icon-relax@2x.png",
	"relax.childrensplayarea": "querywin-icon-relax@2x.png",
	"relax.lounge": "querywin-icon-relax@2x.png",
	"relax.prayerroom": "querywin-icon-relax@2x.png",
	"relax.seating": "querywin-icon-relax@2x.png",
	"relax.tv": "querywin-icon-relax@2x.png",
	restroom: "querywin-icon-restroom@2x.png",
	"restroom.family": "querywin-icon-restroom@2x.png",
	"restroom.female": "querywin-icon-restroomwomen@2x.png",
	"restroom.male": "querywin-icon-restroommen@2x.png",
	"restroom.unisex": "querywin-icon-restroom@2x.png",
	security: "querywin-icon-security@2x.png",
	"security.agricultureinspection": "querywin-icon-security@2x.png",
	"security.checkpoint": "querywin-icon-security@2x.png",
	"security.customs": "querywin-icon-security@2x.png",
	"security.employee": "querywin-icon-security@2x.png",
	"security.exit": "querywin-icon-exit@2x.png",
	services: "querywin-icon-services@2x.png",
	"services.aed": "querywin-icon-services@2x.png",
	"services.animalreliefarea": "querywin-icon-services@2x.png",
	"services.atm": "querywin-icon-atm@2x.png",
	"services.bank": "querywin-icon-services@2x.png",
	"services.chargingstation": "querywin-icon-services@2x.png",
	"services.currencyexchange": "querywin-icon-services@2x.png",
	"services.currencyexchange.dollar": "querywin-icon-services@2x.png",
	"services.currencyexchange.euro": "querywin-icon-services@2x.png",
	"services.currencyexchange.pound": "querywin-icon-services@2x.png",
	"services.directory": "querywin-icon-airportinfo@2x.png",
	"services.drinkingfountain": "querywin-icon-services@2x.png",
	"services.firstaid": "querywin-icon-services@2x.png",
	"services.information": "querywin-icon-airportinfo@2x.png",
	"services.lottery": "querywin-icon-services@2x.png",
	"services.mailbox": "querywin-icon-services@2x.png",
	"services.mobilityassistance": "querywin-icon-wheelchair@2x.png",
	"services.mothersroom": "querywin-icon-services@2x.png",
	"services.movie": "querywin-icon-movierental@2x.png",
	"services.newspaper": "querywin-icon-services@2x.png",
	"services.police": "querywin-icon-services@2x.png",
	"services.prepaidcard": "querywin-icon-services@2x.png",
	"services.rentalcard": "querywin-icon-services@2x.png",
	"services.shoeshine": "querywin-icon-shoeshine@2x.png",
	"services.smokingarea": "querywin-icon-smoking@2x.png",
	"services.specialservices": "querywin-icon-services@2x.png",
	"services.waterrefill": "querywin-icon-services@2x.png",
	shop: "querywin-icon-shop@2x.png",
	ticketing: "querywin-icon-ticketing@2x.png",
	"ticketing.selfcheckinkiosk": "querywin-icon-ticketing@2x.png",
	transport: "querywin-icon-train@2x.png",
	"transport.passengerpickup": "querywin-icon-car@2x.png",
	"transport.sharedride": "querywin-icon-car@2x.png",
	"transport.shuttle": "querywin-icon-bus@2x.png",
	"transport.shuttle.public": "querywin-icon-bus@2x.png",
	"transport.shuttle.secure": "querywin-icon-bus@2x.png",
	"transport.taxi": "querywin-icon-car@2x.png",
	"transport.train": "querywin-icon-train@2x.png",
	"transport.valet": "querywin-icon-car@2x.png"
};
//#endregion
//#region src/functional.ts
/**
* @module Functional
*/
var functional_exports = /* @__PURE__ */ __exportAll({
	alphabetizeWithExceptions: () => alphabetizeWithExceptions,
	arrayToLookupTable: () => arrayToLookupTable,
	arrayToLookupTableUsingPath: () => arrayToLookupTableUsingPath,
	assocPathUnlessNil: () => assocPathUnlessNil,
	assocUsingFunc: () => assocUsingFunc,
	capitalizeFirst: () => capitalizeFirst,
	chain: () => chain,
	concatAll: () => concatAll,
	concatAtPath: () => concatAtPath,
	concatAtProp: () => concatAtProp,
	condNotNil: () => condNotNil,
	debounce: () => debounce,
	filterReturningIndices: () => filterReturningIndices,
	groupByProp: () => groupByProp,
	hasExpectedKeys: () => hasExpectedKeys,
	hasPath: () => hasPath,
	headAndTail: () => headAndTail,
	map: () => map$1,
	mapRemovingNils: () => mapRemovingNils,
	mapWithIndex: () => mapWithIndex,
	matchStructure: () => matchStructure,
	matchStructures: () => matchStructures,
	max: () => max,
	memoizeOnceWith: () => memoizeOnceWith,
	memoizeOnceWithObject: () => memoizeOnceWithObject,
	mergeAtPath: () => mergeAtPath,
	mergeOmittingUndefined: () => mergeOmittingUndefined,
	mergeWithProp: () => mergeWithProp,
	min: () => min,
	nearlyEqualObjects: () => nearlyEqualObjects,
	omitAtPath: () => omitAtPath,
	overPath: () => overPath,
	overProp: () => overProp,
	path: () => path$1,
	pathIs: () => pathIs,
	pathOrThrow: () => pathOrThrow,
	pathString: () => pathString,
	pathStringEq: () => pathStringEq,
	pipe: () => pipe,
	pipeP: () => pipeP,
	pipeWhileNotNil: () => pipeWhileNotNil,
	pluckPath: () => pluckPath,
	rejectWithIndex: () => rejectWithIndex,
	removeCanonicalProperties: () => removeCanonicalProperties,
	removeNilProperties: () => removeNilProperties,
	removeNils: () => removeNils,
	renameKey: () => renameKey,
	renameKeys: () => renameKeys,
	replaceElementWithSameId: () => replaceElementWithSameId,
	splitAlternating: () => splitAlternating,
	stringify: () => stringify,
	textTable: () => textTable,
	throwIf: () => throwIf,
	throwUnless: () => throwUnless,
	updateMany: () => updateMany,
	updateMatching: () => updateMatching,
	warnIfSlow: () => warnIfSlow,
	withoutAtPath: () => withoutAtPath,
	withoutUsing: () => withoutUsing
});
/**
* @sig headAndTail :: [a] -> [a, [a]]
*/
const headAndTail = (a) => [R.head(a), R.tail([...a])];
/**
* Add an index to the default R.map method
* @func mapWithIndex
* @sig mapWithIndex :: ((a -> b), [a]) -> [b]) -> (((a, Number) -> b), [a]) -> [b])
*/
const mapWithIndex = R.addIndex(R.map);
/**
* add an index to the default R.reject method
* @func rejectWithIndex
* @sig rejectWithIndex :: ((a, Number) -> Boolean), [a]) -> [a]
*/
const rejectWithIndex = R.addIndex(R.reject);
/**
* Return the indices of every element of array for which f returned something "truthy"
* @sig Filterable f => (array → Boolean) → f array → [Number]
*/
const filterReturningIndices = (f, array) => {
	const result = [];
	for (let i = 0; i < array.length; i++) {
		const item = array[i];
		if (item !== void 0 && f(item)) result.push(i);
	}
	return result;
};
/**
* Same as R.path, except:
*
* - it's not curried
* - any key which is a negative number assumes that the object at that point in the path is an array to
*   be referenced from the *end*
*
* @example path(['features',  1], { features: ['a', 'b', 'c'] } => 'b'
* @example path(['features', -1], { features: ['a', 'b', 'c'] } => 'c'
* @example path(['features', -2], { features: ['a', 'b', 'c'] } => 'b'
* @example path(['missing',  -2], { features: ['a', 'b', 'c'] } => undefined
*
* @sig path :: ([String], Object) -> *
*/
const path$1 = (keys, o) => {
	let current = o;
	for (let i = 0; i < keys.length; i++) {
		let key = keys[i];
		if (key === void 0) return void 0;
		if (current === null || current === void 0 || typeof current !== "object") return void 0;
		if (typeof key === "number" && key < 0) {
			const lenVal = Reflect.get(current, "length");
			key = (typeof lenVal === "number" ? lenVal : 0) + key;
		}
		current = Reflect.get(current, key);
		if (!current) return void 0;
	}
	return current;
};
const pathString = R.curry((path, o) => {
	if (o === null || o === void 0 || typeof o !== "object") return void 0;
	return R.path(path.split("."), o);
});
const pathStringEq = R.curry((path, val, o) => {
	if (o === null || o === void 0 || typeof o !== "object") return false;
	return R.pathEq(val, path.split("."), o);
});
/**
* Concat an array of arrays into a single output
* @sig concatAll :: [[*]] -> [*]
*/
const concatAll = (as) => R.reduce((acc, a) => acc.concat(a), [], as);
/**
* Given function f, return function g that will call f after a delay (in ms)--but reset the timer each time g is called
* @sig debounce :: (Function, Number) -> Function
*/
function debounce(f, delay) {
	let timeout = null;
	return function toBeCalledLater(...args) {
		const _this = this;
		const later = () => {
			timeout = null;
			f.apply(_this, args);
		};
		if (timeout) clearTimeout(timeout);
		timeout = setTimeout(later, delay);
	};
}
/**
* Variation of R.merge that omits any key whose value is undefined
* @sig mergeOmittingUndefined :: (Object, Object) -> Object
*/
const mergeOmittingUndefined = (o1, o2) => R.pickBy((v) => !Object.is(v, void 0), R.mergeRight(o1, o2));
/**
* Update specific indices of an array by calling a function f on each of them
* @sig updateMany :: ([Number], a -> a, [a]) -> [a]
*/
const updateMany = (f, indices, a) => R.reduce((acc, index) => R.adjust(index, f, acc), [...a], indices);
/**
* Update the specific elements of an array that pass a filter function by calling function f on each of them
* @sig updateMatching :: ((a -> a), (a -> b), list) => {
*/
const updateMatching = (filter, f, list) => updateMany(f, filterReturningIndices(filter, list), list);
/**
* Extend the idea of "plucking" a value from an object so it uses a path rather than a simple property
* @sig pluckPath :: ([String], o) -> [*]
*/
const pluckPath = (path, o) => R.map(R.path([...path]), [...o]);
/**
* Create an object from an array of objects, by plucking each element's 'field'
* @sig String -> [Object] -> Object
*
* @example
*
*    arrayToLookupTable('id')([   ->  {
*        { id: 1, a: 4},     ->      1: { id: 1, a: 4},
*        { id: 2, a: 5 }     ->      2: { id: 2, a: 5 }
*    ])                      ->  }
*/
const arrayToLookupTable = (field) => (o) => {
	const keys = R.pluck(field, o).map((k) => String(k));
	return R.zipObj(keys, [...o]);
};
/**
* Create an object from an array of objects, by plucking each element's 'field'
* @sig ([String], [Object]) -> Object
*
* @example
*
*    arrayToLookupTable(['properties', 'id'], [{
*        { properties: { id: 1, a: 4 }},    ->      1: { properties: { id: 1, a: 4 }},
*        { properties: { id: 2, a: 5 }}     ->      2: { properties: { id: 2, a: 5 }}
*    ])                                     ->  }
*/
const arrayToLookupTableUsingPath = (path, o) => {
	const pluckedPath = R.path([...path]);
	const keys = R.map(pluckedPath, [...o]).map((k) => String(k));
	return R.zipObj(keys, [...o]);
};
const withinTolerance = (n1, n2, tolerance) => Math.abs(n1 - n2) <= tolerance;
const _objectsHaveSameKeys = (o1, o2) => R.equals(R.keys(o1).sort(), R.keys(o2).sort());
const _nearlyEqualAtKey = (o1, o2, tolerance) => (key) => {
	const v1 = o1[key];
	const v2 = o2[key];
	if (typeof v1 === "number" && typeof v2 === "number") return withinTolerance(v1, v2, tolerance);
	if (typeof v1 === "object" && v1 !== null && typeof v2 === "object" && v2 !== null) return nearlyEqualObjects(v1, v2, tolerance);
	return R.equals(v1, v2);
};
/**
* Are two objects (including arrays) equal, where 'equals' is relaxed to allow a tolerance?
* @sig nearlyEqualObjects :: (Object, Object, Number) -> Boolean
*
*/
const nearlyEqualObjects = (o1, o2, tolerance) => {
	if (!_objectsHaveSameKeys(o1, o2)) return false;
	const o1Record = { ...o1 };
	const o2Record = { ...o2 };
	const keys = Object.keys(o1Record);
	const checkKey = _nearlyEqualAtKey(o1Record, o2Record, tolerance);
	return keys.every(checkKey);
};
/**
* Split an array into two arrays; the contents of the first result will include elements 0, 2... of the original
* array and the second result will include elements 1, 3... of the original array
* @sig splitAlternating :: [*] -> [[*], [*]]
*/
const splitAlternating = (a) => {
	const l = [];
	const r = [];
	for (let i = 0; i < a.length; i++) {
		const item = a[i];
		if (item === void 0) continue;
		if (i % 2 === 0) l.push(item);
		else r.push(item);
	}
	return [l, r];
};
/**
* return the max of the array
* @sig max :: [*] -> *
*/
const max = (a) => {
	let m = Number.NEGATIVE_INFINITY;
	for (let i = 0; i < a.length; i++) {
		const v = a[i];
		if (v !== void 0 && v > m) m = v;
	}
	return m;
};
/**
* return the min of the array
* @sig max :: [*] -> *
*/
const min = (a) => {
	let m = Number.POSITIVE_INFINITY;
	for (let i = 0; i < a.length; i++) {
		const v = a[i];
		if (v !== void 0 && v < m) m = v;
	}
	return m;
};
/**
* Alphabetize an array of strings, *ignoring* alphabetization for any string that appears in the array of exceptions
* This is useful because the AI Plugin requires that the key 'type' appear first
* @sig alphabetizeWithExceptions :: [String] -> [String] -> [String]
*/
const alphabetizeWithExceptions = (exceptions) => {
	const lookup = R.fromPairs(mapWithIndex((s, i) => [s, i - 1e4], exceptions));
	return (array) => {
		const compare = (s1, s2) => {
			const lookup1 = lookup[s1];
			const lookup2 = lookup[s2];
			if (lookup1 && !lookup2) return -1;
			if (lookup2 && !lookup1) return 1;
			const c1 = lookup1 ?? s1;
			const c2 = lookup2 ?? s2;
			if (c1 < c2) return -1;
			if (c1 > c2) return 1;
			return 0;
		};
		return array.sort(compare);
	};
};
/**
* JSON stringify an object, but order keys (nearly) alphabetically and put all "coordinates" on one line
* alphabetizationExceptions defines exceptions, and defaults to ['type'] because the AI Plugin requires
* the 'type' to be the first entry in some cases
* @sig (Object, [String]) -> JsonString
*/
const stringify = (o, alphabetizationExceptions = ["type"]) => {
	const isObject = (x) => typeof x === "object" && x !== null;
	const alphabetizer = alphabetizeWithExceptions(alphabetizationExceptions);
	const replacer = (key, value) => {
		const sortKeys = (valueObj) => {
			const o2 = {};
			const sorted = alphabetizer(Object.keys(valueObj));
			for (const k of sorted) o2[k] = valueObj[k];
			return o2;
		};
		if (key === "coordinates") return JSON.stringify(value);
		if (isObject(value) && !Array.isArray(value) && value) return sortKeys(value);
		return value;
	};
	return JSON.stringify(o, replacer, 2).replace(/"coordinates":\s*"(.*)"/g, "\"coordinates\": $1");
};
/**
* Does o have all the requiredKeys, zero or more of the optional keys and no other keys?
* @sig hasExpectedKeys :: ([String], [String], o) -> true|{ missingRequiredKeys:, unexpectedKeys }
*/
const hasExpectedKeys = (requiredKeys, optionalKeys, o) => {
	const keys = Object.keys(o);
	const required = [...requiredKeys];
	const optional = [...optionalKeys];
	const missingRequiredKeys = R.difference(required, keys);
	const unexpectedKeys = R.difference(keys, R.concat(required, optional));
	return missingRequiredKeys.length + unexpectedKeys.length ? {
		missingRequiredKeys,
		unexpectedKeys
	} : true;
};
/**
* Remove all the values in target for which a key/value pair matches one from the canonical object
* @sig (Object, Object) -> Object
*/
const removeCanonicalProperties = (canonical, target) => {
	const fn = (acc, key) => target[key] === canonical[key] ? acc : R.assoc(key, target[key], acc);
	const keys = Object.keys(target);
	return R.reduce(fn, {}, keys);
};
/**
* Replace the key in an object with a new name
* @sig renameKey :: (String, String, Object) -> Object
*/
const renameKey = (from, to, o) => {
	const value = R.prop(from, o);
	let result = R.omit([from], o);
	result = R.assoc(to, value, result);
	return result;
};
/**
* Rename each key in an object by passing the key to a passed-in function
* @sig renameKeys :: ((String -> String), Object) -> Object
*/
const renameKeys = (renameFunc, o) => R.reduce((acc, key) => {
	acc[renameFunc(key)] = o[key];
	return acc;
}, {}, Object.keys(o));
/**
* Add 'toAdd' to the object at o[prop]
* @sig mergeWithProp :: (String, Object, Object) -> Object
* @example mergeWithProp('f', { a: 3, d: 4 }, { f: { a: 1, b: 2 }})) -> { f: { a: 3, b: 2, d: 4 }}
*/
const mergeWithProp = (prop, toAdd, o) => {
	const lens = R.lensProp(prop);
	const oRecord = o;
	return R.over(lens, (old) => {
		const base = old && typeof old === "object" ? old : {};
		return R.mergeRight(base, toAdd);
	}, oRecord);
};
/**
* Add 'toAdd' to the array at o[prop]
* @sig concatAtProp :: (String, [a], Object) -> Object
* @example concatAtProp('a', [3, 4], { a: [1, 2] })) -> { a: [1, 2, 3, 4] }
*/
const concatAtProp = (prop, toAdd, o) => {
	const lens = R.lensProp(prop);
	return R.over(lens, (old) => R.concat(Array.isArray(old) ? old : [], [...toAdd]), o);
};
/**
* Add 'toAdd' to the array at o[prop]
* @sig concatAtProp :: (String, [a], Object) -> Object
* @example concatAtPath(['a', 'b'], [3, 4], { a: { b: [1, 2] }})) -> { a: { b: [1, 2, 3, 4] }}
*/
const concatAtPath = (path, toAdd, o) => R.over(R.lensPath([...path]), (old) => R.concat(Array.isArray(old) ? old : [], [...toAdd]), o);
/**
* Omit an element of an object nested at the given path
* @sig :: ([String], Object) -> Object
* @example omitAtPath(['a', b'], { a: { b: 1, c: 2 }} -> { a: { c: 2 }}
*/
const omitAtPath = (path, o) => {
	const toOmit = R.last(path);
	const prefixPath = R.dropLast(1, path);
	return R.over(R.lensPath([...prefixPath]), R.omit(toOmit !== void 0 ? [toOmit] : []), o);
};
/**
* Remove toRemove from the array found at the path in o
* @sig :: ([String], [a], Object) -> Object
* @example withoutAtPath(['a', b'], [1, 3], { a: { b: [1, 2, 3] }} -> { a: { b: [2] }}
*/
const withoutAtPath = (path, toRemove, o) => R.over(R.lensPath([...path]), R.without([...toRemove]), o);
/**
* Group an array of objects using the specified property to group on (variation of R.groupBy)
* @sig (String, [Object]) -> { k: [Object] }
* @example
*  groupByProp('a',
*      [
*          { a: 1, b: 1},
*          { a: 1, b: 2 },
*          { a: 2, b: 3 }
*      ])
*
*  -> {
*          1: [{ a: 1, b: 1}, { a: 1, b: 2 }],
*          2: [{ a: 2, b: 3 }],
*     }
*/
const groupByProp = (prop, array) => {
	const grouped = R.groupBy((o) => String(o[prop]), [...array]);
	const result = {};
	for (const k of Object.keys(grouped)) {
		const v = grouped[k];
		if (v !== void 0) result[k] = v;
	}
	return result;
};
const assocPathUnlessNil = (path, value, o) => R.isNil(value) ? o : R.assocPath([...path], value, o);
const hasPath = (path, o) => {
	const leafObject = R.path([...R.dropLast(1, path)], o);
	if (!leafObject || typeof leafObject !== "object") return false;
	const leafProperty = R.last(path);
	if (leafProperty === void 0) return false;
	return R.has(leafProperty, leafObject);
};
const replaceElementWithSameId = (pathToId, element, array) => {
	const elementId = R.path([...pathToId], element);
	const first = array.filter((e) => R.path([...pathToId], e) === elementId)[0];
	if (!first) return void 0;
	return R.update(R.indexOf(first, array), element, [...array]);
};
const overProp = (prop, func, o) => R.over(R.lensProp(prop), func, o);
const overPath = (path, func, o) => R.over(R.lensPath([...path]), func, o);
const pipe = (...functions) => {
	return function _piped(...args) {
		const traceFlag = Reflect.get(globalThis, "_tracePipe");
		const trace = typeof traceFlag === "boolean" && traceFlag;
		let nextFunctionInPipe = functions[0];
		if (!nextFunctionInPipe) return void 0;
		let result = nextFunctionInPipe(...args);
		if (trace) console.log(`pipe: ${nextFunctionInPipe.name}(${[args]}) -> ${result}`);
		for (let i = 1; i < functions.length; i++) {
			const fn = functions[i];
			if (!fn) continue;
			nextFunctionInPipe = fn;
			const arg = result;
			result = nextFunctionInPipe(result);
			if (trace) console.log(`pipe: ${nextFunctionInPipe.name}(${arg}) -> ${result}`);
		}
		return result;
	};
};
const pipeP = (...functions) => {
	const traceFlagP = Reflect.get(globalThis, "_tracePipeP");
	const shouldTrace = typeof traceFlagP === "boolean" && traceFlagP;
	const possiblyTrace = (func, args, result) => {
		if (shouldTrace) {
			const functionNames = functions.map((f) => f.name);
			console.log(`trace pipeP(${functionNames}): await ${func.name}(${[args]}) -> ${result}`);
		}
	};
	return async function _piped(...args) {
		let arg;
		const first = functions[0];
		if (!first) return void 0;
		let nextFunctionInPipe = first;
		try {
			nextFunctionInPipe = first;
			let result = await nextFunctionInPipe(...args);
			possiblyTrace(nextFunctionInPipe, args, result);
			for (let i = 1; i < functions.length; i++) {
				const fn = functions[i];
				if (!fn) continue;
				nextFunctionInPipe = fn;
				arg = result;
				result = await nextFunctionInPipe(result);
				possiblyTrace(nextFunctionInPipe, arg, result);
			}
			return result;
		} catch (e) {
			possiblyTrace(nextFunctionInPipe, arg, e);
			throw e;
		}
	};
};
const assocUsingFunc = R.curry((prop, func, o) => R.assoc(prop, func(o), o));
/**
* Memoize function "fn" keeping exactly one previous value. If a subsequent call uses the same parameters,
* the previous values is returned, otherwise fn is called and its params and result are stored for next time
*/
function memoizeOnceWith(paramsToStringFunc, fn) {
	let lastStringifiedParams;
	let lastValue;
	return function returned(...args) {
		const stringifiedParams = paramsToStringFunc.apply(this, args);
		if (stringifiedParams !== lastStringifiedParams) {
			lastStringifiedParams = stringifiedParams;
			lastValue = fn.apply(this, args);
		}
		return lastValue;
	};
}
function memoizeOnceWithObject(memoizeIntoObject, fn) {
	let lastMemoizedObject;
	let lastValue;
	const equalKeys = (a, b) => R.equals(R.keys(a), R.keys(b));
	const equalValues = (a, b) => {
		const valuePairs = R.zip(R.values(a), R.values(b));
		return R.all(([l, r]) => l === r, valuePairs);
	};
	const objectsMatchShallow = (a, b) => a !== void 0 && equalKeys(a, b) && equalValues(a, b);
	return function _memoizeOnceWithObject(...args) {
		const memoizedObject = memoizeIntoObject.apply(this, args);
		if (!objectsMatchShallow(lastMemoizedObject, memoizedObject)) {
			lastMemoizedObject = memoizedObject;
			lastValue = fn.apply(this, args);
		}
		return lastValue;
	};
}
const pathIs = R.curry((path, value, o) => R.path([...path], o) === value);
const mergeAtPath = R.curry((path, newValue, o) => R.over(R.lensPath([...path]), (old) => R.mergeRight(old && typeof old === "object" ? old : {}, newValue), o));
const withoutUsing = R.curry((predicateFunction, a) => {
	const result = [];
	for (let i = 0; i < a.length; i++) {
		const item = a[i];
		if (item !== void 0 && !predicateFunction(item)) result.push(item);
	}
	return result;
});
const pathOrThrow = (path, o) => {
	const x = R.path([...path], o);
	if (R.isNil(x)) throw new Error(`Nothing at ${path.join(".")}`);
	else return x;
};
const capitalizeFirst = (s) => {
	const head = R.head(s);
	return `${head !== void 0 ? R.toUpper(head) : ""}${R.tail(s)}`;
};
const condNotNil = (...funcs) => (arg) => {
	for (let i = 0; i < funcs.length; i++) {
		const fn = funcs[i];
		if (!fn) continue;
		const result = fn(arg);
		if (result) return result;
	}
};
const is = (constructor, val) => {
	if (val === null || val === void 0) return false;
	if (Reflect.get(Object(val), "constructor") === constructor) return true;
	if (Reflect.has(constructor, Symbol.hasInstance) ? Reflect.apply(Reflect.get(constructor, Symbol.hasInstance), constructor, [val]) : false) return true;
	if (constructor === Object && typeof val === "object") return true;
	return false;
};
const isNil = (x) => x == null;
const flatten = (list) => {
	const flatt = (list2) => {
		const result = [];
		for (let i = 0; i < list2.length; i++) {
			const item = list2[i];
			if (Array.isArray(item)) {
				const value = flatt(item);
				for (let j = 0; j < value.length; j++) {
					const v = value[j];
					if (v !== void 0) result[result.length] = v;
				}
			} else result[result.length] = item;
		}
		return result;
	};
	return flatt(list);
};
function map$1(fn, arrayOrObject) {
	if (Array.isArray(arrayOrObject)) return arrayOrObject.map(fn);
	const keys = Object.keys(arrayOrObject);
	const out = {};
	for (const key of keys) {
		const v = Reflect.get(arrayOrObject, key);
		if (v !== void 0) out[key] = fn(v);
	}
	return out;
}
const chain = (fn, list) => {
	const flat = flatten(list.map(fn));
	const result = [];
	for (const v of flat) result.push(v);
	return result;
};
const matchStructure = (structure, o, exact = false) => {
	const matchOne = ([k, rhs]) => {
		const recurse = typeof rhs === "object" && rhs !== null;
		const literal = rhs !== null && typeof rhs !== "function";
		if (!R.has(k, o)) return false;
		const value = o[k];
		if (recurse) {
			if (typeof value !== "object" || value === null) return false;
			const recurseInto = (obj) => {
				const out = {};
				for (const key of Object.keys(obj)) out[key] = Reflect.get(obj, key);
				return out;
			};
			return matchStructure(recurseInto(rhs), recurseInto(value), exact);
		}
		if (literal) return value === rhs;
		if (typeof rhs === "function") return is(rhs, value);
		return false;
	};
	if (exact && Object.keys(structure).length !== Object.keys(o).length) return false;
	return Object.entries(structure).every(matchOne);
};
const matchStructures = (structureProcessPairs, errorMessage) => (o) => {
	for (let i = 0; i < structureProcessPairs.length; i++) {
		const pair = structureProcessPairs[i];
		if (!pair) continue;
		const [structure, processor] = pair;
		if (matchStructure(structure, o)) return processor;
	}
	console.warn(errorMessage(o));
	return {
		error: true,
		feature: o,
		message: errorMessage(o)
	};
};
const warnIfSlow = (f, threshold) => (...args) => {
	const start = Date.now();
	const result = f(...args);
	const duration = Date.now() - start;
	if (duration > threshold) console.warn("S L O W ", `${f.name}(${args.map((a) => String(a))})`, duration);
	return result;
};
const throwIf = (b, errorMessage) => {
	if (b) throw new Error(errorMessage);
	return b;
};
const throwUnless = (b, errorMessage) => {
	if (!b) throw new Error(errorMessage);
	return b;
};
const _isObjectNotArray = (o) => !Array.isArray(o) && o !== null && typeof o === "object";
const _isntNil = (o) => !isNil(o);
const removeNilProperties = (o) => {
	const result = {};
	Object.entries(o).forEach(([k, v]) => {
		const newV = _isObjectNotArray(v) ? removeNilProperties(v) : v;
		if (_isntNil(newV)) result[k] = newV;
	});
	return result;
};
const removeNils = R.reject(R.isNil);
const mapRemovingNils = R.pipe(R.map, removeNils);
const pipeWhileNotNil = R.pipeWith((f, x) => R.isNil(x) ? x : f(x));
const textTable = (objects, keys = R.uniq(R.chain(R.keys, [...objects])).map((k) => String(k))) => {
	const lengthOfLongest = (strings) => R.reduce((acc, v) => Math.max(acc, String(v).length), 0, strings);
	const padToLength = (length) => (v) => String(v).padEnd(length);
	const padTextInColumn = (key) => {
		const valuesForKey = R.map(R.propOr("", key), [...objects]);
		const valuesIncludingHeader = R.prepend(key, valuesForKey);
		const longest = lengthOfLongest(valuesIncludingHeader);
		return R.map(padToLength(longest), valuesIncludingHeader);
	};
	const paddedColumns = R.map(padTextInColumn, [...keys]);
	const compositeColumns = () => {
		const rows = R.transpose(paddedColumns);
		return R.map(R.join(" | "), rows);
	};
	return compositeColumns();
};
//#endregion
//#region src/math.ts
var math_exports = /* @__PURE__ */ __exportAll({
	degreesToRadians: () => degreesToRadians,
	greatCircleDistance: () => greatCircleDistance$1,
	isBetween: () => isBetween
});
/**
* @module math
*/
/**
* @sig degreesToRadians :: Number -> Number
*/
const degreesToRadians = (degrees) => degrees * (Math.PI / 180);
/**
* Calculates the great-circle distance (in meters) between two Lat/Lngs
* @sig greatCircleDistance :: (Lat, Lng, Lat, Lng) -> Number
*/
const greatCircleDistance$1 = (lat1, lng1, lat2, lng2) => {
	const RADIUS = 6371e3;
	const phi1 = degreesToRadians(lat1);
	const phi2 = degreesToRadians(lat2);
	const deltaPhi = degreesToRadians(lat2 - lat1);
	const deltaLamba = degreesToRadians(lng2 - lng1);
	const a = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) + Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLamba / 2) * Math.sin(deltaLamba / 2);
	return RADIUS * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
};
/**
* Is x between low and high (inclusive on both ends)?
* @sig isBetween :: (Number, Number, Number) -> Boolean
*/
const isBetween = (low, x, high) => low <= x && x <= high;
//#endregion
//#region src/data-model/create-typed-constructor.ts
const namedFunction = (name, fn) => new Function("fn", `return function ${name}(){ return fn.apply(this,arguments)}`)(fn);
function createSchemaBasedConstructor(typeName) {
	const schema = Vms[`${typeName}`];
	if (!schema) throw new Error(`Schema '${typeName}' not found.`);
	const parameters = schema.constructorParameterOrder;
	const isObject = schema.type === "object";
	const checkFieldsPassedInAreAlsoInSchema = () => {
		if (!schema.properties || !parameters) throw new Error(`Schema '${typeName}' must define both 'properties' and 'constructorParameterOrder' for object construction.`);
		const fieldsInSchema = keys(schema.properties).sort();
		if (!equals(fieldsInSchema, clone(parameters).sort())) throw new Error(`Schema '${typeName}' has a mismatch between the 'properties' and 'constructorParameterOrder' keys.properties: ${JSON.stringify(fieldsInSchema)} constructorParameterOrder: ${JSON.stringify(parameters)}Every property name must appear in the constructorParameterOrder`);
	};
	if (parameters) checkFieldsPassedInAreAlsoInSchema();
	const prototype = {};
	const validateSchema = createValidator(schema, ...values(Vms));
	function _objectConstructorFunction(...inputValues) {
		const constructedValue = zipAddingPrototype(prototype, parameters ?? [], inputValues);
		const errors = validateSchema(constructedValue);
		if (errors) {
			const [firstError = ""] = errors;
			throw new Error(errors.length === 1 ? `${typeName}${firstError.trim()}` : `${typeName}\n${errors.join("\n")}`);
		}
		return constructedValue;
	}
	function _arrayConstructorFunction(...inputValues) {
		const constructedArray = [...inputValues];
		const errors = validateSchema(constructedArray);
		addInvisiblePropertyToObject(constructedArray, "constructor", constructorFunction);
		if (errors) {
			const [firstError = ""] = errors;
			throw new Error(errors.length === 1 ? `${typeName}${firstError.trim()}` : `${typeName}\n${errors.join("\n")}`);
		}
		return constructedArray;
	}
	const constructorFunction = namedFunction(typeName, isObject ? _objectConstructorFunction : _arrayConstructorFunction);
	addInvisiblePropertyToObject(prototype, "constructor", constructorFunction);
	constructorFunction.prototype = prototype;
	const fromObject = (sourceObject, disallowExtraneous) => {
		const safeParameters = parameters ?? [];
		if (disallowExtraneous) {
			const extraKeys = difference(keys(sourceObject), safeParameters).join(", ");
			if (extraKeys.length) throw new Error(`${typeName}.from() doesn't allow extraneous fields: ${extraKeys}`);
		}
		const fieldValue = (fieldName) => sourceObject[fieldName];
		return _objectConstructorFunction(...safeParameters.map(fieldValue));
	};
	const fromArray = (sourceArray) => {
		return _arrayConstructorFunction(...sourceArray);
	};
	return Object.assign(constructorFunction, {
		from: isObject ? fromObject : fromArray,
		"@@type": typeName,
		prototype
	});
}
const zipAddingPrototype = (proto, keyNames, inputValues) => {
	const obj = Object.create(proto);
	keyNames.forEach((keyName, index) => obj[keyName] = inputValues[index]);
	return obj;
};
const addInvisiblePropertyToObject = (obj, prop, value) => Object.defineProperty(obj, prop, {
	value,
	enumerable: false,
	writable: false,
	configurable: false
});
//#endregion
//#region src/data-model/latlng.ts
/**
* @module LatLng
*
* @see LatLng
*/
const baseLatLng = createSchemaBasedConstructor("LatLng");
/**
*
* (principally because proj4 expects points to have (x,y) fields)
* @sig toXY :: (LatLng) -> { x: Lng, y:  Lat }
*/
const toXY = (latLng) => ({
	x: latLng.lng,
	y: latLng.lat
});
/**
*
* (principally because proj4 expects points to have (x,y) fields)
* @sig fromXY :: { x: Number, y:  Number } -> LatLng
*/
const fromXY = ({ x, y }) => baseLatLng.from({
	lng: x,
	lat: y
});
/**
*
* (principally because proj4 expects points to have (x,y) fields)
* @sig fromXY :: [Lng, Lat] -> LatLng
*/
const fromArray$2 = (a) => {
	const [lng, lat] = a;
	if (lng === void 0 || lat === void 0) throw new Error("LatLng.fromArray: array must have at least 2 elements");
	return baseLatLng.from({
		lng,
		lat
	});
};
/**
* @sig fromLatAndLng :: (Lat, Lng) -> LatLng
*/
const fromLatAndLng$1 = (lat, lng) => baseLatLng.from({
	lat,
	lng
});
/**
* @sig fromLatitudeAndLongitude :: ({ latitude: Number, longitude: Number }) -> LatLng
*/
const fromLatitudeAndLongitude = ({ latitude, longitude }) => baseLatLng.from({
	lat: latitude,
	lng: longitude
});
/**
* Calculate the Great-Circle distance (in meters) between two LatLngs
*
* @sig distance :: (LatLng, LatLng) -> Number
*/
const greatCircleDistance = (p1, p2) => greatCircleDistance$1(p1.lat, p1.lng, p2.lat, p2.lng);
/**
* @sig toString :: LatLng -> String
*
* NOTE: assigned via Object.assign (rather than `LatLng.toString = ...`) to avoid conflicting with
* Function.prototype.toString's `() => string` signature.
*/
const LatLng = Object.assign(baseLatLng, {
	toXY,
	fromXY,
	fromArray: fromArray$2,
	fromLatAndLng: fromLatAndLng$1,
	fromLatitudeAndLongitude,
	greatCircleDistance,
	toString: (latLng) => `[${latLng.lat}, ${latLng.lng}]`
});
//#endregion
//#region src/data-model/theme.ts
/**
* Types
*
* A Theme is an object where each key is the name of a category and each value is a CategoryTheme
*
* CategoryTheme
*
* A CategoryTheme is a collection of default drawing properties for a category. A Feature can override the
* default properties of its theme. The properties in a CategoryTheme are:
*
*    fillColor
*    strokeColor
*    strokeAlign
*    strokeWidth
*    textColor
*    fontName
*    fontSize
*
* During asset generation a Feature has its size reduced by having any properties that match the canonical
* values for its category. At runtime, before the Feature is added to Mapbox, canonical properties are added back in
*
* @module DataTypes.Theme
*
*/
var theme_exports = /* @__PURE__ */ __exportAll({
	badgesInTheme: () => badgesInTheme,
	enrichWithDefaults: () => enrichWithDefaults,
	removeDefaultPropertiesForCategoryFromFeature: () => removeDefaultPropertiesForCategoryFromFeature,
	themeForCategory: () => themeForCategory
});
const themeForMissingCategory = {
	fillColor: "#0000FF",
	fontName: "Open Sans",
	fontSize: 10,
	strokeAlign: "center",
	strokeColor: "#00FF00",
	strokeWidth: .5,
	textColor: "#00FF00"
};
const themeDefaults = {
	fillColor: "#C9C9C9",
	fontName: "Open Sans",
	fontSize: 3,
	strokeAlign: "center",
	strokeColor: "#000000",
	strokeWidth: 1,
	textColor: "#000000"
};
const removeDefaultPropertiesFromFeature = (theme, properties) => {
	const category = properties.category;
	if (!category) throw new Error(`no category found in ${JSON.stringify(properties)}`);
	const categoryTheme = theme[category];
	if (!categoryTheme) throw new Error(`no theme found for category ${category}`);
	return omit(keys(categoryTheme), properties);
};
const removeDefaultPropertiesForCategoryFromFeature = (theme, feature) => {
	const category = path(["properties", "category"], feature);
	return category && category.length ? assoc("properties", removeDefaultPropertiesFromFeature(theme, feature.properties), feature) : feature;
};
const themeForCategory = (theme, category) => {
	const categoryTheme = theme[category];
	if (!categoryTheme) console.warn(`Unable to find category '${category}' in theme: using "missing" category instead`);
	return categoryTheme || themeForMissingCategory;
};
const enrichWithDefaults = (theme) => {
	const defaults = mergeRight(themeDefaults, theme.default || {});
	return map((o) => mergeRight(defaults, o), theme);
};
const badgesInTheme = (theme) => {
	return without("default", keys(filter((c) => !!c.hasBadge, theme)));
};
//#endregion
//#region src/data-model/feature.ts
var feature_exports = /* @__PURE__ */ __exportAll({
	boundingBoxOfFeatures: () => boundingBoxOfFeatures,
	categoriesForFeatures: () => categoriesForFeatures,
	center: () => center,
	groupByPolygon: () => groupByPolygon,
	hasBadge: () => hasBadge,
	hasText: () => hasText,
	isLineString: () => isLineString,
	isPolygon: () => isPolygon,
	isSymbol: () => isSymbol,
	isTouch: () => isTouch,
	logosForFeatures: () => logosForFeatures,
	openLocationBody: () => openLocationBody,
	promoteIdFromSubidProperty: () => promoteIdFromSubidProperty,
	symbolFeatureFromData: () => symbolFeatureFromData,
	update: () => update,
	updateFeatureFromEntity: () => updateFeatureFromEntity,
	updateFeatureFromTheme: () => updateFeatureFromTheme,
	updatePolygonFeatureForEntity: () => updatePolygonFeatureForEntity
});
const isGeometry = (v) => !!v && typeof v === "object" && "type" in v && "coordinates" in v;
const isFeatureLike$2 = (v) => {
	if (!v || typeof v !== "object") return false;
	const o = v;
	return isGeometry(o.geometry) && !!o.properties && typeof o.properties === "object";
};
const asFeature$1 = (v) => {
	if (isFeatureLike$2(v)) return v;
	throw new Error("asFeature: value is not a FeatureLike");
};
const isString = (v) => typeof v === "string";
const callLatLngHelper$1 = (name, ...args) => {
	const fn = LatLng[name];
	if (typeof fn !== "function") throw new Error(`LatLng.${name} is not a function`);
	return fn(...args);
};
const fromArray$1 = (a) => callLatLngHelper$1("fromArray", a);
const hasText = (feature) => feature.properties.text;
const hasBadge = (feature) => feature.properties.showBadge;
const isPolygon = (feature) => feature.geometry.type === "Polygon";
const isLineString = (feature) => feature.geometry.type === "LineString";
const isSymbol = (feature) => feature.geometry.type === "Point";
const isTouch = (feature) => feature.properties.isTouch;
const centerOfLinearRing = (linearRing) => {
	const arr = Array.isArray(linearRing) ? linearRing : [];
	const lngs = R.pluck(0, R.tail(arr));
	const lats = R.pluck(1, R.tail(arr));
	return fromArray$1([R.mean(lngs), R.mean(lats)]);
};
const center = (feature) => {
	if (!feature) return;
	const geometry = feature.geometry;
	const { type: geometryType } = geometry;
	switch (geometry.type) {
		case "Point": return fromArray$1(geometry.coordinates);
		case "LineString": return centerOfLinearRing(geometry.coordinates);
		case "Polygon": return centerOfLinearRing(geometry.coordinates[0] ?? []);
		default: throw new Error(`Feature.center not handling type ${geometryType}`);
	}
};
/**
* Return the canonical names of every image in the GeoJson data
* @sig imagesInGeoJson :: [Feature] -> [String]
*/
const categoriesForFeatures = (features) => {
	const stringCategories = R.map(R.path(["properties", "category"]), features).filter(isString);
	return R.uniq(stringCategories);
};
/**
* Return the canonical names of every logo in the GeoJson data
* @sig logosForFeatures :: [Feature] -> [String]
*/
const logosForFeatures = (features) => {
	const logos = R.map(R.path(["properties", "logo"]), features);
	const stringLogos = R.filter(isString, logos);
	return R.uniq(stringLogos);
};
const boundingBoxOfFeatures = (features) => {
	const emptyBoundingBox = {
		w: Number.POSITIVE_INFINITY,
		e: Number.NEGATIVE_INFINITY,
		n: Number.NEGATIVE_INFINITY,
		s: Number.POSITIVE_INFINITY
	};
	const flattenCoordinates = (coordinates) => {
		if (!Array.isArray(coordinates)) return [];
		if (R.is(Number, coordinates[0])) return coordinates;
		return R.chain(flattenCoordinates, coordinates);
	};
	const expandBoundigBoxToIncludeFeature = (boundingBox, feature) => {
		const [lngs, lats] = splitAlternating(flattenCoordinates(feature.geometry.coordinates));
		return {
			w: Math.min(boundingBox.w, min(lngs)),
			e: Math.max(boundingBox.e, max(lngs)),
			n: Math.max(boundingBox.n, max(lats)),
			s: Math.min(boundingBox.s, min(lats))
		};
	};
	return R.reduce(expandBoundigBoxToIncludeFeature, emptyBoundingBox, features);
};
const _themeHasNoSuchCategory = (category) => !category || category.length === 0;
const _categoryHasNoFillOrStroke = (category) => !category.fillColor || !category.strokeColor;
const _addAttributesFromTheme = (theme, feature, attributes) => {
	const category = feature.properties.category;
	if (_themeHasNoSuchCategory(category)) return feature;
	const themeCategory = themeForCategory(theme, category);
	if (_categoryHasNoFillOrStroke(themeCategory)) return feature;
	return asFeature$1(mergeWithProp("properties", R.pick(attributes, themeCategory), feature));
};
const updateFeatureFromTheme = (theme) => (feature) => _addAttributesFromTheme(theme, feature, ["fillColor", "strokeColor"]);
const updateFeatureFromEntity = (entityIdNameMap) => (feature) => {
	const id = R.path(["properties", "id"], feature);
	const entityName = isString(id) ? entityIdNameMap[id] : void 0;
	return entityName ? R.assocPath(["properties", "text"], entityName, feature) : feature;
};
const update = ({ theme, feature, showBadge = false, showText = false, category, text, latLng }) => {
	const _updateSymbolAttributes = (existingText) => {
		if (!showBadge && !showText) throw new Error("Feature.update: showBadge and/or showText has to be set");
		feature = asFeature$1(R.assocPath(["properties", "showBadge"], !!showBadge, feature));
		if (showText && existingText) feature = asFeature$1(R.assocPath(["properties", "text"], existingText, feature));
		if (!showText) feature = asFeature$1(omitAtPath(["properties", "text"], feature));
		if (!R.isNil(latLng)) feature = asFeature$1(R.assocPath(["geometry", "coordinates"], [latLng.lng, latLng.lat], feature));
		return _addAttributesFromTheme(theme, feature, [
			"textColor",
			"fontName",
			"fontSize"
		]);
	};
	const _updateLineAttributes = () => _addAttributesFromTheme(theme, feature, [
		"strokeColor",
		"strokeWidth",
		"strokeAlign"
	]);
	const _updatePolygonAttributes = () => {
		const props = isTouch(feature) ? ["fillColor"] : [
			"fillColor",
			"strokeColor",
			"strokeWidth",
			"strokeAlign"
		];
		return _addAttributesFromTheme(theme, feature, props);
	};
	text = showText ? text : void 0;
	feature = asFeature$1(assocPathUnlessNil(["properties", "category"], category, feature));
	if (isSymbol(feature)) return _updateSymbolAttributes(text);
	if (isPolygon(feature)) return _updatePolygonAttributes();
	if (isLineString(feature)) return _updateLineAttributes();
	throw new Error("feature is not polygon, line, text or badge");
};
const symbolFeatureFromData = ({ theme, entityId, featureId, category, text, latLng, features, aiLayer = "poi", showBadge = false, showText = false }) => {
	if (!showBadge && !showText) throw new Error("symbolFeatureFromData: showBadge and/or text must be defined");
	if (!featureId && features) featureId = createFeatureId(features, entityId);
	let feature = {
		id: featureId,
		type: "Feature",
		geometry: {
			type: "Point",
			coordinates: [latLng.lng, latLng.lat]
		},
		properties: {
			type: "Text",
			align: "center",
			id: entityId,
			subid: featureId,
			category,
			aiLayer
		}
	};
	if (showBadge) feature = R.assocPath(["properties", "showBadge"], true, feature);
	if (showText) {
		feature = R.assocPath(["properties", "text"], text, feature);
		feature = _addAttributesFromTheme(theme, feature, [
			"textColor",
			"fontName",
			"fontSize"
		]);
	}
	return feature;
};
const createFeatureId = (features, entityId) => {
	const otherFeatureIdsForEntity = R.map(R.path(["properties", "subid"]))(R.filter((f) => f.properties.id === entityId, features));
	if (otherFeatureIdsForEntity.length === 0) return `${entityId}a`;
	const stringIds = otherFeatureIdsForEntity.filter(isString);
	const lastExistingSubid = R.last(stringIds.sort());
	if (!lastExistingSubid) return `${entityId}a`;
	const [prefix, suffix] = [R.dropLast(1, lastExistingSubid), R.takeLast(1, lastExistingSubid)];
	return prefix + String.fromCharCode(suffix.charCodeAt(0) + 1);
};
const promoteIdFromSubidProperty = (feature) => asFeature$1(R.assoc("id", feature.properties.subid, feature));
const groupByPolygon = (features) => R.groupBy((f) => isPolygon(f) ? "polygon" : "notPolygon", features);
const updatePolygonFeatureForEntity = (entity, feature, theme) => {
	feature = update({
		feature,
		category: entity.category,
		theme
	});
	feature = asFeature$1(R.assocPath(["properties", "id"], entity.id, feature));
	feature = asFeature$1(omitAtPath(["properties", "subid"], feature));
	feature = asFeature$1(Object.fromEntries(Object.entries(feature).filter(([k]) => k !== "$loki")));
	return feature;
};
const openLocationBody = (entity, featuresForEntity, oldFeature) => {
	return {
		entity,
		features: R.map((f) => {
			const stripped = asFeature$1(Object.fromEntries(Object.entries(f).filter(([k]) => ![
				"$loki",
				"meta",
				"id"
			].includes(k))));
			stripped.properties = R.omit([
				"type",
				"fontName",
				"fontSize",
				"levelId",
				"ordinalId",
				"structureId",
				"venueId"
			], stripped.properties);
			return stripped;
		}, featuresForEntity),
		oldFeatureId: oldFeature.properties.id
	};
};
//#endregion
//#region src/data-model/entity.ts
var entity_exports = /* @__PURE__ */ __exportAll({
	convertGateToLower: () => convertGateToLower,
	createDefaultEntity: () => createDefaultEntity,
	enrichCreatedEntity: () => enrichCreatedEntity,
	enrichUpdateEntity: () => enrichUpdateEntity,
	getEntityCategory: () => getEntityCategory,
	getIcon: () => getIcon,
	guessLatLng: () => guessLatLng,
	instructionsForAddEntity: () => instructionsForAddEntity,
	instructionsForRemoveEntity: () => instructionsForRemoveEntity,
	instructionsForUpdateEntity: () => instructionsForUpdateEntity,
	isClosed: () => isClosed
});
const isNumber = (v) => typeof v === "number";
const isLatLng = (v) => {
	if (!v || typeof v !== "object") return false;
	const o = v;
	return typeof o.lat === "number" && typeof o.lng === "number";
};
const isFeatureLike$1 = (v) => {
	if (!v || typeof v !== "object") return false;
	const o = v;
	return !!o.geometry && typeof o.geometry === "object" && !!o.properties && typeof o.properties === "object";
};
const asFeature = (v) => {
	if (isFeatureLike$1(v)) return v;
	throw new Error("asFeature: value is not a FeatureLike");
};
const _centerOfPolygon = (polygonFeature) => {
	const coordinates = R.splitEvery(2, R.flatten([polygonFeature.geometry.coordinates]));
	const xsRaw = R.map(R.head, coordinates);
	const ysRaw = R.map(R.last, coordinates);
	const xs = xsRaw.filter(isNumber);
	const ys = ysRaw.filter(isNumber);
	return [(min(xs) + max(xs)) / 2, (min(ys) + max(ys)) / 2];
};
const callLatLngHelper = (name, ...args) => {
	const fn = LatLng[name];
	if (typeof fn !== "function") throw new Error(`LatLng.${name} is not a function`);
	return fn(...args);
};
const guessLatLng = (featuresForEntity, addingText) => {
	const badgeFeature = R.find((f) => !!hasBadge(f), featuresForEntity);
	const textFeature = R.find((f) => !!hasText(f), featuresForEntity);
	const polygonFeature = R.find((f) => isPolygon(f), featuresForEntity);
	if (!badgeFeature && !textFeature && !polygonFeature) throw new Error("addFeature: no latLng was passed in and there's no way to guess it");
	const copyPointFromBadge = addingText && !!badgeFeature;
	const copyPointFromText = !addingText && !!textFeature;
	let lng;
	let lat;
	if (copyPointFromBadge && badgeFeature?.centerPoint) [lng, lat] = [badgeFeature.centerPoint.x, badgeFeature.centerPoint.y];
	else if (copyPointFromText && textFeature) {
		const coords = textFeature.geometry.coordinates;
		if (Array.isArray(coords)) [lng, lat] = [isNumber(coords[0]) ? coords[0] : void 0, isNumber(coords[1]) ? coords[1] : void 0];
	} else if (polygonFeature) [lng, lat] = _centerOfPolygon(polygonFeature);
	const result = callLatLngHelper("fromLatAndLng", lat, lng);
	if (!isLatLng(result)) throw new Error("guessLatLng: unexpected result from LatLng.fromLatAndLng");
	return result;
};
const instructionsForAddEntity = ({ theme, featuresForEntity, entity, showBadge, showName, latLng, features }) => {
	const guessed = latLng ?? guessLatLng(featuresForEntity, !!showName);
	const { id: entityId, category, name: text } = entity;
	return { addFeatures: [symbolFeatureFromData({
		category,
		entityId,
		features,
		latLng: guessed,
		showBadge,
		showText: showName,
		text,
		theme
	})] };
};
const instructionsForRemoveEntity = (featuresForEntity, removePolygon) => {
	if (removePolygon) return { removeFeatures: featuresForEntity };
	const setFeatureProperties = (toAdd) => (f) => {
		const result = overProp("properties", (props) => R.mergeRight(props && typeof props === "object" ? props : {}, toAdd), f);
		if (!isFeatureLike$1(result)) throw new Error("setFeatureProperties: result is not a FeatureLike");
		return result;
	};
	const polygons = R.filter((f) => !!pathIs(["geometry", "type"], "Polygon")(f), featuresForEntity);
	return {
		removeFeatures: R.reject((f) => !!pathIs(["geometry", "type"], "Polygon")(f), featuresForEntity),
		updateFeatures: R.map(setFeatureProperties({
			category: "unoccupied",
			fillColor: "#DFE5E7",
			strokeColor: "#CBD0D9"
		}), polygons)
	};
};
const _computeUpdateDisposition = ({ featuresForEntity, showBadge, showName }) => {
	const symbolFeature = R.find((f) => f.geometry.type === "Point", featuresForEntity);
	const symbolHasBadge = R.path(["properties", "showBadge"], symbolFeature);
	const symbolHasText = R.path(["properties", "text"], symbolFeature);
	const removeSymbol = symbolHasBadge && symbolHasText && !showName && !showBadge || symbolHasBadge && !symbolHasText && !showBadge || symbolHasText && !symbolHasBadge && !showName;
	const addSymbol = showName || showBadge;
	const shouldRemove = symbolFeature && removeSymbol && !addSymbol;
	const isIllegalRemove = shouldRemove && featuresForEntity.length === 1;
	const shouldAdd = !symbolFeature && addSymbol;
	if (isIllegalRemove) return "illegalRemove";
	if (shouldRemove) return "remove";
	if (shouldAdd) return "add";
	if (symbolFeature) return "update";
	return "noop";
};
const instructionsForUpdateEntity = ({ theme, entity, featuresForEntity, showBadge, showName, latLng }) => {
	const instructions = {
		addFeatures: [],
		removeFeatures: [],
		updateFeatures: []
	};
	const polygonFeature = R.find((f) => f.geometry.type === "Polygon", featuresForEntity);
	const symbolFeature = R.find((f) => f.geometry.type === "Point", featuresForEntity);
	const symbolHasBadge = symbolFeature ? hasPath(["properties", "showBadge"], symbolFeature) : false;
	const symbolHasText = symbolFeature ? hasPath(["properties", "text"], symbolFeature) : false;
	const symbolHasLogo = symbolFeature ? hasPath(["properties", "logo"], symbolFeature) : false;
	const symbolHasBadgeOrLogo = symbolHasBadge || symbolHasLogo;
	const resolvedShowBadge = R.isNil(showBadge) ? !!symbolHasBadgeOrLogo : !!showBadge;
	const resolvedShowName = R.isNil(showName) ? !!symbolHasText : !!showName;
	const pushInstructionsForUpdateFeature = (feature) => {
		let updated = update({
			theme,
			feature,
			showBadge: resolvedShowBadge,
			showText: resolvedShowName,
			category: entity.category,
			text: entity.name,
			latLng
		});
		if (!resolvedShowBadge || symbolHasLogo) updated = asFeature(omitAtPath(["properties", "showBadge"], updated));
		instructions.updateFeatures.push(updated);
	};
	if (polygonFeature) pushInstructionsForUpdateFeature(polygonFeature);
	const illegal = "Can't remove the last feature related to an entity via updateElement; use removeAiEntity instead";
	const dataForAdd = {
		theme,
		featuresForEntity,
		entity,
		showBadge: resolvedShowBadge,
		showName: resolvedShowName,
		latLng,
		features: featuresForEntity
	};
	switch (_computeUpdateDisposition({
		featuresForEntity,
		showBadge: resolvedShowBadge,
		showName: resolvedShowName
	})) {
		case "add": return instructionsForAddEntity(dataForAdd);
		case "remove":
			if (!symbolFeature) throw new Error(illegal);
			return instructionsForRemoveEntity([symbolFeature]);
		case "update":
			if (symbolFeature) pushInstructionsForUpdateFeature(symbolFeature);
			break;
		case "illegalRemove": throw new Error(illegal);
		case "noop": break;
		default: throw new Error(`updateEntity: don't understand what to do with ${JSON.stringify(entity)}`);
	}
	return instructions;
};
const createDefaultEntity = (latLng, level, structure, ordinalNumber, includeBadge, showName, security) => {
	const { lat, lng } = latLng;
	const entity = {
		category: "shop",
		floor: `${ordinalNumber} ${level.details ?? ""}`,
		name: "Enter POI Name",
		ordinalNumber,
		position: {
			lat,
			lng,
			levelId: level.id
		},
		showName,
		terminal: structure.name,
		uuid: `${level.venueId}_NEW`,
		latLng,
		levelId: level.id,
		venueId: level.venueId
	};
	if (includeBadge) entity.showBadge = true;
	if (security) entity.security = security;
	return entity;
};
const checkEntityForMapChanges = (entity, updatedEntity) => {
	return !!(!!updatedEntity.name && entity.name !== updatedEntity.name || !!updatedEntity.category && entity.category !== updatedEntity.category || typeof updatedEntity.id === "string" && updatedEntity.id.includes("#"));
};
const enrichUpdateEntity = (entity, updateEntityObject, featuresForEntity, levelId) => {
	const point = R.find((feature) => feature.geometry.type === "Point", featuresForEntity);
	const showBadge = R.pathOr(false, ["properties", "showBadge"], point);
	const showName = R.hasPath(["properties", "text"], point);
	return R.mergeRight(updateEntityObject, {
		levelId,
		showBadge,
		showName,
		hasMapChanged: checkEntityForMapChanges(entity, updateEntityObject.entity)
	});
};
const getEntityCategory = (entity) => entity.category;
const getIcon = (entity) => entity.icon;
const convertToLower = (entity) => entity.category === "Gate" ? R.assoc("category", "gate", entity) : entity;
const convertGateToLower = (entities) => R.map(convertToLower, entities);
const enrichCreatedEntity = (entity) => convertToLower(entity);
const isClosed = (entity) => {
	const id = R.propOr("", "id", entity);
	return typeof id === "string" ? id.match(/#/) : null;
};
//#endregion
//#region src/data-model/geojson.ts
/**
* Functions for manipulating GeoJson objects
*
* @module GeoJson
*/
var geojson_exports = /* @__PURE__ */ __exportAll({
	addEntity: () => addEntity,
	geoJsonFromFeatures: () => geoJsonFromFeatures,
	removeEntity: () => removeEntity,
	removePropFromFeatureCollection: () => removePropFromFeatureCollection,
	replacePolygon: () => replacePolygon,
	updateEntity: () => updateEntity
});
const isFeatureLike = (v) => {
	if (!v || typeof v !== "object") return false;
	const o = v;
	return !!o.geometry && typeof o.geometry === "object" && !!o.properties && typeof o.properties === "object";
};
/**
* Return a GeoJson object from a list of GeoJson features and an optional id
* @sig geoJsonFromFeatures :: ([Feature], String) -> GeoJson
*/
const geoJsonFromFeatures = (features, id) => id ? {
	type: "FeatureCollection",
	features,
	id
} : {
	type: "FeatureCollection",
	features
};
const _processFeatureInstructions = ({ features, instructions }) => {
	const updateFeatures = (fs, toAdd) => {
		const replace = (acc, updated) => replaceElementWithSameId(["properties", "subid"], updated, acc) ?? acc;
		return reduce(replace, fs, toAdd);
	};
	const addFeatures = (fs, toAdd) => concat(fs, toAdd);
	const removeFeatures = (fs, toRemove) => without(toRemove, fs);
	let result = features;
	result = addFeatures(result, instructions.addFeatures ?? []);
	result = updateFeatures(result, instructions.updateFeatures ?? []);
	result = removeFeatures(result, instructions.removeFeatures ?? []);
	return result;
};
/**
* Add a (symbol) Feature for an Entity
* addEntity :: (Theme, [Feature], Entity, Boolean, Boolean, LatLng) -> [Feature]
*/
const addEntity = ({ theme, features, entity, showBadge, showName, latLng }) => {
	return _processFeatureInstructions({
		features,
		instructions: instructionsForAddEntity({
			theme,
			entity,
			showBadge,
			showName,
			latLng,
			features,
			featuresForEntity: features
		})
	});
};
/**
* updateAiEntity :: ({ Theme, [Feature], Entity, Boolean, Boolean, LatLng }) -> [Feature]
*/
const updateEntity = ({ theme, features, entity, showBadge, showName, latLng }) => {
	const featuresForEntity = filter((f) => f.properties.id === entity.id, features);
	if (featuresForEntity.length === 0) return features;
	return _processFeatureInstructions({
		features,
		instructions: instructionsForUpdateEntity({
			theme,
			featuresForEntity,
			entity,
			showBadge,
			showName,
			latLng
		})
	});
};
/**
* Remove all the Features for an Entity -- EXCEPT polygons!
* removeAiEntity :: ({ [Feature], EntityId }) -> [Feature]
*/
const removeEntity = ({ features, entityId }) => {
	return _processFeatureInstructions({
		features,
		instructions: instructionsForRemoveEntity(filter((f) => !!pathIs(["properties", "id"], entityId)(f), features))
	});
};
const replacePolygon = (features, oldId, newFeatures) => {
	const index = findIndex(pathEq(oldId, ["properties", "id"]))(features);
	if (index === -1) return features;
	const reduced = reject((f) => f.properties.id === oldId, features);
	return insertAll(index, Array.isArray(newFeatures) ? newFeatures : [newFeatures], reduced);
};
const removePropFromFeatureCollection = (prop) => (featureCollection) => {
	const { features } = featureCollection;
	const removeProp = (feature) => {
		const result = omitAtPath(["properties", prop], feature);
		if (!isFeatureLike(result)) throw new Error("removeProp: result is not a FeatureLike");
		return result;
	};
	return assoc("features", features.map(removeProp), featureCollection);
};
//#endregion
//#region src/data-model/latlng-bounds.ts
var latlng_bounds_exports = /* @__PURE__ */ __exportAll({
	empty: () => empty,
	isPointInBounds: () => isPointInBounds
});
const fromLatAndLng = (lat, lng) => {
	const fn = LatLng.fromLatAndLng;
	if (typeof fn !== "function") throw new Error("LatLng.fromLatAndLng is not a function");
	return fn(lat, lng);
};
const empty = () => ({
	nw: fromLatAndLng(0, 0),
	se: fromLatAndLng(0, 0)
});
/**
* Is p in bounds?
* @sig isPointInBounds -> (LatLng, BoundingBox) -> Boolean
*/
const isPointInBounds = (p, boundingBox) => {
	const { n, w, s, e } = boundingBox;
	return isBetween(w, p.lng, e) && isBetween(s, p.lat, n);
};
//#endregion
//#region src/data-model/position.ts
var position_exports = /* @__PURE__ */ __exportAll({ fromLatLngAndLevel: () => fromLatLngAndLevel });
const fromLatLngAndLevel = (latLng, levelId) => assoc("levelId", levelId, latLng);
//#endregion
//#region src/data-model/json-schema-tools.ts
const tools = (typeName) => {
	const schema = Vms[typeName];
	if (!schema) throw new Error(`No schema found for ${typeName}`);
	const keysFromSchema = keys(schema.properties ?? {});
	const ajvValidate = createValidator(schema, ...values(Vms));
	const validate = (o) => {
		const errors = ajvValidate(o);
		if (errors) throw new Error(errors.join("\n"));
		return o;
	};
	const from = (sourceObject) => validate(pick(keysFromSchema, sourceObject));
	return {
		validate,
		from
	};
};
//#endregion
//#region src/data-model/wayfinding-segment.ts
var wayfinding_segment_exports = /* @__PURE__ */ __exportAll({
	from: () => from,
	fromArray: () => fromArray,
	toGeoJson: () => toGeoJson,
	validate: () => validate
});
const { validate, from } = tools("WayfindingSegment");
const _toLineStringGeoJson = (wayfindingSegment) => ({
	type: "Feature",
	geometry: {
		type: "LineString",
		coordinates: wayfindingSegment.coordinates
	},
	properties: {
		segmentType: wayfindingSegment.segmentType,
		venueId: wayfindingSegment.venueId,
		ordinalId: wayfindingSegment.ordinalId
	}
});
const _toBadgeGeoJson = (wayfindingSegment) => (badge) => ({
	type: "Feature",
	geometry: {
		type: "Point",
		coordinates: badge.coordinates
	},
	properties: {
		badge: badge.canonicalName,
		entityId: badge.entityId,
		venueId: wayfindingSegment.venueId,
		ordinalId: wayfindingSegment.ordinalId
	}
});
const toGeoJson = (wayfindingSegment) => {
	return prepend(_toLineStringGeoJson(wayfindingSegment), map(_toBadgeGeoJson(wayfindingSegment), wayfindingSegment.badges));
};
const fromArray = (pojos) => map(from, pojos);
//#endregion
//#region src/data-model/bounding-box.ts
const BoundingBox = createSchemaBasedConstructor("BoundingBox");
BoundingBox.fromBoundsPolygon = (boundsPolygon) => {
	const headNum = (xs) => head(xs) ?? 0;
	const lastNum = (xs) => last(xs) ?? 0;
	const lats = map(headNum, boundsPolygon);
	const lngs = map(lastNum, boundsPolygon);
	const w = min(lngs);
	const e = max(lngs);
	return BoundingBox(max(lats), e, min(lats), w);
};
//#endregion
//#region src/data-model/bounds-polygon.ts
const BoundsPolygon = createSchemaBasedConstructor("BoundsPolygon");
//#endregion
//#region src/data-model/latlng-array.ts
const LatLngArray = createSchemaBasedConstructor("LatLngArray");
//#endregion
//#region src/data-model/level.ts
const Level = createSchemaBasedConstructor("Level");
Level.getLevelsForOrdinal = (ordinalId, levels) => filter((l) => l.ordinalId === ordinalId, levels);
Level.getLevelIdsForOrdinal = (ordinalId, levels) => {
	return pluck("id", filter((l) => l.ordinalId === ordinalId, levels));
};
//#endregion
//#region src/data-model/ordinal.ts
/**
* OrdinalId
*
* Id for an Ordinal. A higher OrdinalId is above a lower OrdinalId,
* but the range of OrdinalIds at a Venue is not necessarily
* contiguous or all positive.
* OrdinalIds typically *don't* match the ClFloorId for a level.
*
*
*
* @module DataTypes.Ordinal
*/
const getDefaultOrdinalForVenue = (ordinals) => {
	const findDefaultOrdinal = (o) => !!o.isDefaultForVenue;
	return R.find(findDefaultOrdinal, ordinals);
};
const Ordinal = Object.assign(createSchemaBasedConstructor("Ordinal"), {
	LANDSCAPE_BACKGROUND: "landscape-background",
	getDefaultOrdinalForVenue
});
//#endregion
//#region src/data-model/structure.ts
const getDefaultStructureForVenue = (structures) => {
	const findDefaultStructure = (s) => !!s.isDefaultForVenue;
	return find(findDefaultStructure, structures);
};
const Structure = Object.assign(createSchemaBasedConstructor("Structure"), { getDefaultStructureForVenue });
//#endregion
//#region src/data-model/venue.ts
/**
* @module Venue
*/
const enrichVenueDetailsFromAiJson = (venueId, venueData, aiJson) => {
	return assocPath(["fixedGeometry", "viewBox"], {
		x: 0,
		y: 0,
		width: path(["properties", "width"], aiJson),
		height: path(["properties", "height"], aiJson)
	}, venueData);
};
const getVenueCenter = (venue) => {
	const { venueCenter } = venue;
	const { lat: latitude, lng: longitude } = venueCenter;
	return {
		latitude,
		longitude
	};
};
const Venue = Object.assign(createSchemaBasedConstructor("Venue"), {
	enrichVenueDetailsFromAiJson,
	getVenueCenter
});
//#endregion
//#region src/font-metrics.ts
var font_metrics_exports = /* @__PURE__ */ __exportAll({
	height: () => height,
	topToBaseline: () => topToBaseline,
	yOffsetForPixiText: () => yOffsetForPixiText
});
const fontMetrics = {
	"Open Sans": [
		{
			min: 0,
			max: 3.2,
			topToBaseline: 2.28,
			height: 3.78
		},
		{
			min: 3.2,
			max: 4,
			topToBaseline: 2.432,
			height: 4.032
		},
		{
			min: 4,
			max: 4.311,
			topToBaseline: 3.04,
			height: 5.04
		},
		{
			min: 4.311,
			max: 6.5,
			topToBaseline: 3.276,
			height: 5.432
		},
		{
			min: 6.5,
			max: 3e3,
			topToBaseline: 4.94,
			height: 5.19
		}
	],
	"Open Sans Semibold": [{
		min: 0,
		max: 3e3,
		topToBaseline: 6,
		height: 12
	}]
};
const interpolate = (fontName, metric, fontSize) => {
	const fontMetric = fontMetrics[fontName];
	if (!fontMetric) {
		console.error(`No font metrics for ${fontName}`);
		return fontSize;
	}
	const row = find((r) => r.min <= fontSize && fontSize < r.max, fontMetric);
	if (!row) return fontSize;
	return row[metric];
};
const topToBaseline = (fontFamily, fontSize) => interpolate(fontFamily, "topToBaseline", fontSize);
const height = (fontFamily, fontSize) => interpolate(fontFamily, "height", fontSize);
const yOffsetForPixiText = (fontName, fontSize, text) => {
	const lineCount = text.split(/\n/).length;
	const lineHeight = height(fontName, fontSize);
	return lineCount === 1 ? lineHeight / 2 : -(lineCount - 2) * lineHeight - topToBaseline(fontName, fontSize);
};
//#endregion
//#region src/data-model/image-upload.ts
var image_upload_exports = /* @__PURE__ */ __exportAll({ convertImage: () => convertImage });
const convertImage = async (file) => new Promise((resolve) => {
	const uploadFile = {
		contentType: file.type,
		fileName: replace(/\s/g, "", file.name)
	};
	const reader = new FileReader();
	reader.addEventListener("load", () => {
		const result = typeof reader.result === "string" ? reader.result : "";
		uploadFile.data = replace(/[^,]+,/, "", file.type === "image/jpeg" ? piexif.remove(result) : result);
		resolve(uploadFile);
	});
	reader.readAsDataURL(file);
	return uploadFile;
});
//#endregion
//#region src/data-model/map-note/map-note-filter.ts
var map_note_filter_exports = /* @__PURE__ */ __exportAll({
	fromDefault: () => fromDefault,
	setStatus: () => setStatus,
	titleFor: () => titleFor,
	toggleType: () => toggleType
});
const fromDefault = () => ({
	status: "open",
	type: {
		noType: true,
		mapRequest: true,
		fieldVerify: true
	}
});
const setStatus = (selectedOption, mapNoteFilter) => assoc("status", selectedOption, mapNoteFilter);
const toggleType = (selectedOption, mapNoteFilter) => {
	const checkOption = (val, key) => key === selectedOption ? !mapNoteFilter.type[selectedOption] : val;
	return assoc("type", mapObjIndexed(checkOption, mapNoteFilter.type), mapNoteFilter);
};
const titleFor = (typeProperty) => capitalizeFirst(replace(/([A-Z])/g, " $1", typeProperty));
//#endregion
//#region src/data-model/map-note/map-note.ts
var map_note_exports$1 = /* @__PURE__ */ __exportAll({
	createNewMapNote: () => createNewMapNote,
	createTempMapNotelet: () => createTempMapNotelet,
	deleteNoteFromTotals: () => deleteNoteFromTotals,
	findMapNotelet: () => findMapNotelet,
	isNoType: () => isNoType,
	removeFields: () => removeFields,
	updateMapNotelet: () => updateMapNotelet
});
const createNewMapNote = (updatedFields, venueId, ordinalId, tempMapNotelet, levelId, structureId) => {
	const mapNote = {
		subject: updatedFields.subject,
		noteStatus: "open",
		description: updatedFields.description || "",
		noteType: updatedFields.noteType,
		location: {
			venueId,
			ordinalId: `ordinal: ${ordinalId}`,
			latitude: tempMapNotelet.location.latitude,
			longitude: tempMapNotelet.location.longitude
		}
	};
	if (levelId && structureId) return {
		...mapNote,
		location: {
			...mapNote.location,
			levelId,
			structureId
		}
	};
	if (levelId) return {
		...mapNote,
		location: {
			...mapNote.location,
			levelId
		}
	};
	if (structureId) return {
		...mapNote,
		location: {
			...mapNote.location,
			structureId
		}
	};
	return mapNote;
};
const createTempMapNotelet = (latLng, venueId) => ({
	id: -1,
	noteStatus: "open",
	location: {
		latitude: latLng.lat,
		longitude: latLng.lng,
		venueId
	}
});
const isNoType = (value) => value === "---" ? "noType" : value;
const removeFields = (fieldsToOmit, mapNote) => omit(fieldsToOmit, mapNote);
const findMapNotelet = (noteId, mapNotelets) => find(propEq(noteId, "id"))(mapNotelets);
const updateMapNotelet = (notelet, mapNotelets) => without([notelet], mapNotelets);
const deleteNoteFromTotals = (totals, noteStatus) => assoc(noteStatus, (totals[noteStatus] ?? 0) - 1, totals);
//#endregion
//#region src/data-model/map-note/map-note-comment.ts
var map_note_comment_exports = /* @__PURE__ */ __exportAll({
	findComment: () => findComment,
	updateComment: () => updateComment
});
const findComment = (commentId, mapNoteComments) => find(propEq(commentId, "id"))(mapNoteComments);
const updateComment = (comment, mapNoteComments) => without([comment], mapNoteComments);
//#endregion
//#region src/data-model/map-note/index.ts
var map_note_exports = /* @__PURE__ */ __exportAll({
	MapNote: () => map_note_exports$1,
	MapNoteComment: () => map_note_comment_exports,
	MapNoteFilter: () => map_note_filter_exports
});
//#endregion
//#region src/matrix.ts
/**
* Matrix algebra
*/
var matrix_exports = /* @__PURE__ */ __exportAll({
	concatenate: () => concatenate,
	identityMatrix: () => identityMatrix,
	matrixToPointArray: () => matrixToPointArray,
	matrixToPointObject: () => matrixToPointObject,
	multiply: () => multiply,
	numberToMatrix: () => numberToMatrix,
	pointToMatrix: () => pointToMatrix,
	rotate: () => rotate,
	rotateAroundPoint: () => rotateAroundPoint,
	rotatingMatrix: () => rotatingMatrix,
	scale: () => scale,
	scalingMatrix: () => scalingMatrix,
	toCssTransform: () => toCssTransform,
	toString: () => toString,
	translate: () => translate,
	translationMatrix: () => translationMatrix
});
/**
* Multiply a x b where a and b are each an array of row-ordered numbers.
* If a is an m x n matrix, then b must be an n x p matrix
*
* @sig Matrix a -> Matrix b -> Matrix c
*      Matrix = Array(Array(Number), Array(Number), Array(Number))
*
* @example
*
*     const a = [
*         [1, 2, 3],
*         [4, 5, 6],
*         [7, 8, 9]
*     ]
*
*     const b = [
*         [1, 1, 1],
*         [1, 1, 1],
*         [1, 1, 1]
*     ]
*
*     multiply(a,b) =>
*        [
*            [6,   6,  6],
*            [15, 15, 15],
*            [24, 24, 24]
*        ]
*/
const multiply = curry((a, b) => {
	const aNumRows = a.length;
	const aNumCols = a[0].length;
	const bNumCols = b[0].length;
	const m = new Array(aNumRows);
	for (let r = 0; r < aNumRows; ++r) {
		m[r] = new Array(bNumCols);
		for (let c = 0; c < bNumCols; ++c) {
			m[r][c] = 0;
			for (let i = 0; i < aNumCols; ++i) m[r][c] += a[r][i] * b[i][c];
		}
	}
	return m;
});
const concatenate = (...matrices) => {
	const first = head(matrices);
	if (!first) throw new Error("concatenate requires at least one matrix");
	return reduce((acc, m) => multiply(acc, m), first, tail(matrices));
};
/**
* Returns a translation matrix from dx and dy
*
* @sig (Number, Number) => Matrix
*/
const translationMatrix = (dx, dy) => [
	[
		1,
		0,
		dx
	],
	[
		0,
		1,
		dy
	],
	[
		0,
		0,
		1
	]
];
/**
* Returns a scaling matrix from s
*
* @sig (Number) => Matrix
*/
const scalingMatrix = (s) => [
	[
		s,
		0,
		0
	],
	[
		0,
		s,
		0
	],
	[
		0,
		0,
		1
	]
];
/**
* Translate matrix m by (dx, dy)
*
* @sig (Number, Number) -> Matrix -> Matrix
*/
const translate = curry((dx, dy, m) => multiply(translationMatrix(dx, dy), m));
/**
* Returns a rotation matrix from degrees (clockwise)
*
* @sig Number -> Matrix
*/
const rotatingMatrix = (degrees) => {
	const degreesToRadians = (degrees) => degrees * (Math.PI / 180);
	const radians = degreesToRadians(degrees);
	return [
		[
			Math.cos(radians),
			-Math.sin(radians),
			0
		],
		[
			Math.sin(radians),
			Math.cos(radians),
			0
		],
		[
			0,
			0,
			1
		]
	];
};
/**
* Translate matrix m by (dx, dy)
*
* @sig Number -> Matrix -> Matrix
*/
const rotate = curry((degrees, m) => multiply(rotatingMatrix(degrees), m));
/**
* Scale matrix m by s
*
* @sig (Number) -> Matrix -> Matrix
*/
const scale = curry((s, m) => multiply(scalingMatrix(s), m));
const rotateAroundPoint = (x, y, centerX, centerY, degrees) => {
	const toCenter = translationMatrix(-centerX, -centerY);
	const rotation = rotatingMatrix(degrees);
	return matrixToPointArray(concatenate(translationMatrix(centerX, centerY), rotation, toCenter, pointToMatrix(x, y)));
};
/**
* Translate a point provided in one of three formats into an [x, y] array.
*
* @sig:     (Number a, Number b) -> [a, b]  OR
*           {x:, y:}             -> [x, y]  OR
*           [a, b]               -> [a, b]
*/
const standardizePoint = (a, b) => {
	if (Array.isArray(a)) return [a[0], a[1]];
	if (typeof a === "number") return [a, b];
	if (a && typeof a === "object" && typeof a.x === "number" && typeof a.y === "number") return [a.x, a.y];
};
/**
* Convert a matrix to an array whose first value is x and second value is y
* @sig   Matrix -> Array
*/
const matrixToPointArray = (m) => [m[0][0], m[1][0]];
/**
* Convert a matrix to an object with x and y keys
* @sig   Matrix -> {k:v}
*/
const matrixToPointObject = (m) => ({
	x: m[0][0],
	y: m[1][0]
});
/**
*  a, c, e
*  b, d, f       =>  [a, b, c, d, e, f]
*  0, 0, 1
*
* See http://www.useragentman.com/blog/2011/01/07/css3-matrix-transform-for-the-mathematically-challenged/
*/
const toCssTransform = (m) => [
	m[0][0],
	m[1][0],
	m[0][1],
	m[1][1],
	m[0][2],
	m[1][2]
];
/**
* Return an identity matrix of the given width/height
*/
const identityMatrix = (dimension) => {
	const result = [];
	let i;
	for (i = 0; i < dimension; i++) result.push(Array(dimension).fill(0));
	for (i = 0; i < dimension; i++) result[i][i] = 1;
	return result;
};
/**
* Convert a point to a matrix with 3 rows and one column. Uses standardizePoint to convert multiple formats
*
* @sig:     (Number a, Number b) -> [[a], [b], [1]]  OR
*           {x:, y:}             -> [[x], [y], [1]]  OR
*           [a, b]               -> [[a], [b], [1]]
*/
const pointToMatrix = (x, y) => {
	const point = standardizePoint(x, y);
	if (!point) throw new Error("pointToMatrix: invalid point input");
	return [
		[point[0]],
		[point[1]],
		[1]
	];
};
/**
* Convert a number to a matrix with 3 rows and one column.
*
* @sig:     Number a -> [[a], [a], [1]]
*/
const numberToMatrix = (x) => pointToMatrix(x, x);
const padLeft = (char, padWidth, number) => Array(Math.max(0, padWidth - String(number).length + 1)).join(char) + number;
const toFixed = (decimals, n) => n.toFixed(decimals);
const padToFixed = (char, padWidth, decimals) => (n) => padLeft(char, padWidth, toFixed(decimals, n));
const rowToString = (row) => join(" ", map(padToFixed(" ", 6, 1), row));
const toString = (matrix) => map(rowToString, matrix).join("\n");
//#endregion
//#region src/time-utilities.ts
var time_utilities_exports = /* @__PURE__ */ __exportAll({
	calculateDate: () => calculateDate,
	calculateDateTimeUK: () => calculateDateTimeUK
});
const calculateDate = (datetime) => moment(datetime).format("MMM DD, YYYY");
const calculateDateTimeUK = (datetime) => moment(datetime).format("Do MMM, YYYY @ HH:MM");
//#endregion
//#region src/data-model/upload-image.ts
var upload_image_exports = /* @__PURE__ */ __exportAll({ convertToImage: () => convertToImage });
const convertToImage = (file, image) => ({
	tmpId: crypto.randomUUID(),
	file,
	caption: file.name,
	dimensions: {
		height: image.height,
		width: image.width
	},
	size: file.size,
	type: file.type,
	uri: image.src,
	dateUploaded: new Date(Date.now())
});
//#endregion
export { BoundingBox, BoundsPolygon, category_exports as Category, entity_exports as Entity, feature_exports as Feature, font_metrics_exports as FontMetrics, functional_exports as Functional, geojson_exports as GeoJson, image_upload_exports as ImageUpload, LatLng, LatLngArray, latlng_bounds_exports as LatLngBounds, Level, map_note_exports as MapNote, matrix_exports as Matrix, Ordinal, position_exports as Position, Structure, theme_exports as Theme, time_utilities_exports as TimeUtilities, upload_image_exports as UploadImage, Venue, wayfinding_segment_exports as WayfindingSegment, createSchemaBasedConstructor, math_exports as math };

//# sourceMappingURL=index.mjs.map